import React, {useState, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {
  getScheduledSubprofileStats,
  ScheduledParent,
  ScheduledSubprofileSummary,
  ParentCommand,
} from './core/_requests'

const colors = {
  bgPrimary: '#f5f8fa',
  bgSecondary: '#ffffff',
  bgTertiary: '#e8ecef',
  bgHover: '#e1e5ea',
  textPrimary: '#1e1e2d',
  textSecondary: '#5e6278',
  textMuted: '#a1a5b7',
  accentPrimary: '#3699ff',
  accentSuccess: '#1bc5bd',
  accentWarning: '#ffa800',
  accentDanger: '#f64e60',
  borderColor: 'rgba(0, 0, 0, 0.08)',
}

const ScheduledSubprofilesPage: React.FC = () => {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<ScheduledSubprofileSummary | null>(null)
  const [parents, setParents] = useState<ScheduledParent[]>([])
  const [scheduledCommands, setScheduledCommands] = useState<ParentCommand[]>([])
  const [notScheduledCommands, setNotScheduledCommands] = useState<ParentCommand[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [hoveredCard, setHoveredCard] = useState<number | null>(null)
  const [hoveredRow, setHoveredRow] = useState<string | null>(null)
  const [expandedParent, setExpandedParent] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const res = await getScheduledSubprofileStats()
        if (!cancelled) {
          setSummary(res.data.summary)
          setParents(res.data.parents || [])
          setScheduledCommands(res.data.commands?.scheduled || [])
          setNotScheduledCommands(res.data.commands?.not_scheduled || [])
        }
      } catch (error) {
        console.error('Failed to load scheduled subprofile stats:', error)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const filteredParents = parents.filter((p) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      (p.parent_name || '').toLowerCase().includes(q) ||
      (p.parent_facebook_id || '').toLowerCase().includes(q) ||
      (p.account_email || '').toLowerCase().includes(q) ||
      (p.account_name || '').toLowerCase().includes(q) ||
      (p.account_company || '').toLowerCase().includes(q) ||
      p.children.some((c) => c.name.toLowerCase().includes(q) || c.facebook_id.toLowerCase().includes(q))
    )
  })

  const totalScheduledChildren = filteredParents.reduce((sum, p) => sum + p.scheduled_children_count, 0)

  const statsData = [
    {
      label: 'תתי דפים פעילים (סה"כ)',
      value: summary?.total_active_children || 0,
      icon: 'bi-layers-fill',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    },
    {
      label: 'תתי דפים עם פוסטים היום',
      value: summary?.total_scheduled_children || 0,
      icon: 'bi-calendar-check-fill',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    },
    {
      label: 'דפי אב (צריכים החלפה)',
      value: summary?.total_parents_with_scheduled || 0,
      icon: 'bi-person-fill',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    },
  ]

  return (
    <div style={{padding: 0}}>
      {/* Page Header */}
      <div style={{marginBottom: '32px'}}>
        <h1
          style={{
            fontSize: '24px',
            fontWeight: 600,
            color: colors.textPrimary,
            margin: 0,
          }}
        >
          תתי דפים משובצים (Scheduled Sub-Profiles)
        </h1>
        <p style={{color: colors.textSecondary, marginTop: '8px', fontSize: '14px'}}>
          דפי אב עם תתי דפים פעילים שמשובצים בקמפיינים
        </p>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        {statsData.map((stat, i) => (
          <div
            key={i}
            onMouseEnter={() => setHoveredCard(i)}
            onMouseLeave={() => setHoveredCard(null)}
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              padding: '20px',
              transition: 'all 0.2s ease',
              transform: hoveredCard === i ? 'translateY(-2px)' : 'none',
              border: `1px solid ${hoveredCard === i ? colors.borderColor : 'transparent'}`,
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: stat.gradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '12px',
              }}
            >
              <i className={`bi ${stat.icon}`} style={{fontSize: '20px', color: '#fff'}}></i>
            </div>
            <div
              style={{
                fontSize: '28px',
                fontWeight: 700,
                color: colors.textPrimary,
                marginBottom: '4px',
              }}
            >
              {stat.value.toLocaleString()}
            </div>
            <div style={{fontSize: '13px', color: colors.textSecondary}}>{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Search Bar */}
      <div
        style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          alignItems: 'center',
          background: colors.bgTertiary,
          padding: '12px 16px',
          borderRadius: '8px',
        }}
      >
        <div style={{flex: 1, position: 'relative'}}>
          <i
            className='bi bi-search'
            style={{
              position: 'absolute',
              right: '14px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.textMuted,
              fontSize: '16px',
            }}
          ></i>
          <input
            type='text'
            placeholder='חיפוש לפי שם דף אב, חשבון, אימייל, תת דף...'
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 44px 12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: colors.textPrimary,
              fontSize: '14px',
              outline: 'none',
            }}
          />
        </div>
        <div style={{fontSize: '13px', color: colors.textSecondary}}>
          {filteredParents.length} דפי אב | {totalScheduledChildren} תתי דפים
        </div>
      </div>

      {/* Table */}
      <div
        style={{
          background: colors.bgTertiary,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        {/* Table Header */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr',
            padding: '16px 24px',
            borderBottom: `1px solid ${colors.borderColor}`,
          }}
        >
          {['דף אב', 'חשבון', 'תתי דפים משובצים', 'אונליין', ''].map((header, i) => (
            <div
              key={i}
              style={{
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: colors.textMuted,
              }}
            >
              {header}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {loading ? (
          <div
            style={{
              padding: '60px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${colors.bgHover}`,
                borderTopColor: colors.accentPrimary,
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }}
            ></div>
          </div>
        ) : filteredParents.length === 0 ? (
          <div style={{padding: '60px', textAlign: 'center'}}>
            <i
              className='bi bi-inbox'
              style={{fontSize: '48px', color: colors.textMuted, marginBottom: '16px', display: 'block'}}
            ></i>
            <div style={{fontSize: '18px', fontWeight: 600, color: colors.textPrimary, marginBottom: '8px'}}>
              לא נמצאו תוצאות
            </div>
            <div style={{fontSize: '14px', color: colors.textSecondary}}>
              אין דפי אב עם תתי דפים משובצים
            </div>
          </div>
        ) : (
          filteredParents.map((parent) => {
            const isExpanded = expandedParent === parent.parent_id
            const onlineCount = parent.children.filter((c) => c.is_online).length

            return (
              <React.Fragment key={parent.parent_id}>
                <div
                  onClick={() =>
                    setExpandedParent(isExpanded ? null : parent.parent_id)
                  }
                  onMouseEnter={() => setHoveredRow(parent.parent_id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr',
                    padding: '16px 24px',
                    alignItems: 'center',
                    borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    cursor: 'pointer',
                    background: isExpanded
                      ? 'rgba(54, 153, 255, 0.06)'
                      : hoveredRow === parent.parent_id
                      ? colors.bgHover
                      : 'transparent',
                    transition: 'background 0.15s ease',
                  }}
                >
                  {/* Parent Profile */}
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={{position: 'relative'}}>
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#fff',
                        }}
                      >
                        {parent.parent_name.charAt(0).toUpperCase()}
                      </div>
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '0',
                          right: '0',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: parent.parent_is_online ? colors.accentSuccess : colors.textMuted,
                          border: '2px solid #e8ecef',
                        }}
                      ></div>
                    </div>
                    <div>
                      <div style={{fontWeight: 600, color: colors.textPrimary, fontSize: '14px'}}>
                        {parent.parent_name}
                      </div>
                      {parent.parent_facebook_id && (
                        <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '2px'}}>
                          {parent.parent_facebook_id}
                        </div>
                      )}
                    </div>
                    {parent.parent_is_scheduled && (
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          background: 'rgba(255, 168, 0, 0.15)',
                          color: colors.accentWarning,
                          fontWeight: 600,
                        }}
                      >
                        דף אב: {parent.parent_post_count} פוסטים
                      </span>
                    )}
                  </div>

                  {/* Account */}
                  <div>
                    <div style={{fontWeight: 500, color: colors.textPrimary, fontSize: '13px'}}>
                      {parent.account_name || parent.account_email}
                    </div>
                    {parent.account_company && (
                      <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '1px'}}>
                        {parent.account_company}
                      </div>
                    )}
                    {parent.account_locked && (
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          background: 'rgba(246, 78, 96, 0.15)',
                          color: colors.accentDanger,
                          fontWeight: 600,
                        }}
                      >
                        נעול
                      </span>
                    )}
                  </div>

                  {/* Scheduled Children Count */}
                  <div>
                    <span
                      style={{
                        padding: '4px 12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 700,
                        background: 'rgba(17, 153, 142, 0.15)',
                        color: '#11998e',
                      }}
                    >
                      {parent.scheduled_children_count}
                    </span>
                  </div>

                  {/* Online Count */}
                  <div>
                    {onlineCount > 0 ? (
                      <span
                        style={{
                          padding: '4px 10px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: 'rgba(27, 197, 189, 0.15)',
                          color: colors.accentSuccess,
                        }}
                      >
                        {onlineCount} / {parent.scheduled_children_count}
                      </span>
                    ) : (
                      <span style={{color: colors.textMuted, fontSize: '12px'}}>
                        0 / {parent.scheduled_children_count}
                      </span>
                    )}
                  </div>

                  {/* Expand Arrow */}
                  <div style={{textAlign: 'center'}}>
                    <i
                      className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}
                      style={{color: colors.textMuted, fontSize: '14px'}}
                    ></i>
                  </div>
                </div>

                {/* Expanded Children List */}
                {isExpanded && (
                  <div
                    style={{
                      padding: '0 24px 16px 24px',
                      background: 'rgba(54, 153, 255, 0.03)',
                      borderBottom: `1px solid rgba(0,0,0,0.05)`,
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '10px',
                        paddingTop: '12px',
                      }}
                    >
                      {parent.children.map((child) => (
                        <div
                          key={child._id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '10px 14px',
                            background: colors.bgSecondary,
                            borderRadius: '8px',
                            border: `1px solid ${colors.borderColor}`,
                          }}
                        >
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: child.is_online ? colors.accentSuccess : colors.textMuted,
                              flexShrink: 0,
                            }}
                          ></div>
                          <div style={{flex: 1, minWidth: 0}}>
                            <div
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: colors.textPrimary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {child.name}
                            </div>
                            {child.facebook_id && (
                              <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '2px'}}>
                                {child.facebook_id}
                              </div>
                            )}
                          </div>
                          <div style={{display: 'flex', gap: '6px', flexShrink: 0}}>
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'rgba(54, 153, 255, 0.1)',
                                color: colors.accentPrimary,
                                fontWeight: 600,
                              }}
                            >
                              {child.post_count} פוסטים
                            </span>
                            <span
                              style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'rgba(255, 168, 0, 0.1)',
                                color: colors.accentWarning,
                                fontWeight: 600,
                              }}
                            >
                              {child.campaign_count} קמפיינים
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: '11px',
                              color: colors.textSecondary,
                              flexShrink: 0,
                            }}
                          >
                            {child.posts_per_day}/יום
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Link to account detail */}
                    {parent.account_id && (
                      <div style={{marginTop: '12px', textAlign: 'left'}}>
                        <span
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/admin/accounts/${parent.account_id}`)
                          }}
                          style={{
                            fontSize: '12px',
                            color: colors.accentPrimary,
                            cursor: 'pointer',
                            fontWeight: 600,
                          }}
                        >
                          צפה בחשבון &larr;
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })
        )}
      </div>

      {/* PM2 Commands Section */}
      {!loading && (scheduledCommands.length > 0 || notScheduledCommands.length > 0) && (
        <div style={{marginTop: '32px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
          {/* Scheduled - need to run */}
          <div
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: `1px solid ${colors.borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: colors.accentSuccess}}>
                  <i className="bi bi-play-circle-fill me-2"></i>
                  צריכים לרוץ ({scheduledCommands.length})
                </div>
                <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '2px'}}>
                  פרופילים עם תתי דפים משובצים היום
                </div>
              </div>
              <button
                onClick={() => {
                  const cmds = scheduledCommands.map(c => `export DISPLAY=:0 && pm2 start app-hour-batched.js --name="account_${c.parent_id}" -- --account="${c.account_id}" --username="${c.account_email}" --user="${c.parent_id}"`).join('\n')
                  navigator.clipboard.writeText(cmds)
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'rgba(27, 197, 189, 0.15)',
                  color: colors.accentSuccess,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '11px',
                }}
              >
                <i className="bi bi-clipboard me-1"></i>
                העתק הכל
              </button>
            </div>
            <pre
              style={{
                padding: '12px 20px',
                maxHeight: '400px',
                overflowY: 'auto',
                margin: 0,
                fontSize: '12px',
                fontFamily: 'monospace',
                color: colors.textPrimary,
                direction: 'ltr',
                textAlign: 'left',
                whiteSpace: 'pre',
                userSelect: 'all',
              }}
            >{scheduledCommands.map(c => `export DISPLAY=:0 && pm2 start app-hour-batched.js --name="account_${c.parent_id}" -- --account="${c.account_id}" --username="${c.account_email}" --user="${c.parent_id}"`).join('\n')}</pre>
          </div>

          {/* Not scheduled - can stop */}
          <div
            style={{
              background: colors.bgTertiary,
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '16px 20px',
                borderBottom: `1px solid ${colors.borderColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{fontWeight: 700, fontSize: '14px', color: colors.textMuted}}>
                  <i className="bi bi-pause-circle-fill me-2"></i>
                  לא משובצים היום ({notScheduledCommands.length})
                </div>
                <div style={{fontSize: '11px', color: colors.textMuted, marginTop: '2px'}}>
                  פרופילים עם תתי דפים פעילים אך בלי שיבוצים היום
                </div>
              </div>
              <button
                onClick={() => {
                  const cmds = notScheduledCommands.map(c => `export DISPLAY=:0 && pm2 start app-hour-batched.js --name="account_${c.parent_id}" -- --account="${c.account_id}" --username="${c.account_email}" --user="${c.parent_id}"`).join('\n')
                  navigator.clipboard.writeText(cmds)
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'rgba(160, 174, 192, 0.15)',
                  color: colors.textMuted,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '11px',
                }}
              >
                <i className="bi bi-clipboard me-1"></i>
                העתק הכל
              </button>
            </div>
            <pre
              style={{
                padding: '12px 20px',
                maxHeight: '400px',
                overflowY: 'auto',
                margin: 0,
                fontSize: '12px',
                fontFamily: 'monospace',
                lineHeight: '2',
                background: 'transparent',
                color: colors.textPrimary,
                direction: 'ltr',
                textAlign: 'left',
                whiteSpace: 'pre',
                userSelect: 'all',
              }}
            >{notScheduledCommands.map(c => `export DISPLAY=:0 && pm2 start app-hour-batched.js --name="account_${c.parent_id}" -- --account="${c.account_id}" --username="${c.account_email}" --user="${c.parent_id}"`).join('\n')}</pre>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

export default ScheduledSubprofilesPage
